import { useState } from 'react';
import { Edit, Create, SimpleForm, TextInput, NumberInput, SelectInput, TabbedForm, FormTab, ReferenceInput, useRecordContext, useNotify, useRefresh } from 'react-admin';
import { Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';

const tallInput = { '& .MuiInputBase-root': { minHeight: 48 } };

const ProductTitle = () => {
    const record = useRecordContext();
    return <span>{record ? `${record.article} — ${record.name}` : ''}</span>;
};

const ImageManagement = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [uploading, setUploading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    if (!record || !record.article) return null;
    const count = record.countImages || 0;

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setUploading(true);
        const formData = new FormData();
        Array.from(files).forEach(f => formData.append('files', f));
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/admin/products/${record.article}/images`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            if (!res.ok) throw new Error('Upload failed');
            notify('Изображения загружены', { type: 'success' });
            refresh();
        } catch (err) {
            notify(`Ошибка: ${err.message}`, { type: 'error' });
        }
        setUploading(false);
    };

    const confirmDelete = async () => {
        if (deleteTarget === null) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/admin/products/${record.article}/images/${deleteTarget}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Delete failed');
            notify('Изображение удалено', { type: 'success' });
            setDeleteTarget(null);
            refresh();
        } catch (err) {
            notify(`Ошибка: ${err.message}`, { type: 'error' });
            setDeleteTarget(null);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Array.from({ length: count }, (_, i) => (
                    <Box key={i} sx={{ position: 'relative', width: 120, height: 120 }}>
                        <img src={`/product/image/${record.article}/${i}`}
                            alt={`${record.article}/${i}`}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', border: '1px solid #e5e5e5', borderRadius: 4, background: '#fff' }} />
                        <IconButton onClick={() => setDeleteTarget(i)}
                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', '&:hover': { bgcolor: '#fcc' } }}
                            size="small">
                            <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                    </Box>
                ))}
                {count === 0 && (
                    <Typography color="text.secondary">Нет изображений</Typography>
                )}
            </Box>
            <Button variant="outlined" component="label" disabled={uploading} startIcon={<UploadIcon />}>
                {uploading ? 'Загрузка...' : 'Загрузить изображения'}
                <input type="file" multiple accept=".jpg,.jpeg,.png,.webp" hidden onChange={handleUpload} />
            </Button>

            <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Удалить изображение?</DialogTitle>
                <DialogContent>
                    <Typography>Вы уверены, что хотите удалить это изображение? Действие необратимо.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Отмена</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Удалить</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export const ProductEdit = () => (
    <Edit title={<ProductTitle />}>
        <TabbedForm>
            <FormTab label="Основное">
                <TextInput source="article" label="Артикул" fullWidth sx={tallInput} />
                <TextInput source="name" label="Название" fullWidth sx={tallInput} />
                <TextInput source="fullName" label="Полное название" fullWidth multiline rows={2} sx={tallInput} />
                <TextInput source="type" label="Тип" fullWidth sx={tallInput} />
                <TextInput source="collection" label="Коллекция" fullWidth sx={tallInput} />
                <TextInput source="color" label="Цвет" fullWidth sx={tallInput} />
                <TextInput source="chssis" label="Шасси" fullWidth sx={tallInput} />
                <TextInput source="box" label="Комплектация" fullWidth multiline rows={3} sx={tallInput} />
            </FormTab>
            <FormTab label="Характеристики">
                <NumberInput source="weight" label="Вес (кг)" sx={tallInput} />
                <NumberInput source="volume" label="Объём (м³)" sx={tallInput} />
            </FormTab>
            <FormTab label="Цена и остаток">
                <NumberInput source="price" label="Цена" sx={tallInput} />
                <ReferenceInput source="discountId" reference="discounts" label="Скидка">
                    <SelectInput optionText="name" sx={tallInput} />
                </ReferenceInput>
                <NumberInput source="count" label="Остаток" sx={tallInput} />
            </FormTab>
            <FormTab label="Изображения">
                <ImageManagement />
            </FormTab>
        </TabbedForm>
    </Edit>
);

export const ProductCreate = () => (
    <Create title="Новый товар">
        <SimpleForm>
            <TextInput source="article" label="Артикул" fullWidth sx={tallInput} />
            <TextInput source="name" label="Название" fullWidth sx={tallInput} />
            <TextInput source="fullName" label="Полное название" fullWidth multiline rows={2} sx={tallInput} />
            <TextInput source="type" label="Тип" fullWidth sx={tallInput} />
            <TextInput source="collection" label="Коллекция" fullWidth sx={tallInput} />
            <TextInput source="color" label="Цвет" fullWidth sx={tallInput} />
            <TextInput source="chssis" label="Шасси" fullWidth sx={tallInput} />
            <TextInput source="box" label="Комплектация" fullWidth multiline rows={3} sx={tallInput} />
            <NumberInput source="weight" label="Вес (кг)" sx={tallInput} />
            <NumberInput source="volume" label="Объём (м³)" sx={tallInput} />
            <NumberInput source="price" label="Цена" sx={tallInput} />
            <NumberInput source="count" label="Остаток" sx={tallInput} />
        </SimpleForm>
    </Create>
);
